import { get, isFunction } from 'lodash';
import { getCurrentInstance } from 'vue';

import { sanitizeInput, upperFirst } from '@nova/core';

import notificationHelpers from '@satellite/helpers/notifications';

/**
 * @typedef {import('vue').ComponentInternalInstance} ComponentInternalInstance
 */

/**
 * @typedef {import('@kyvg/vue3-notification').NotificationsOptions} NotificationsOptions
 */

/**
 * @typedef {'success' | 'error' | 'warning'} NotificationType
 */

/**
 * @typedef {(message: string, type: NotificationType, options?: NotificationsOptions) => void} NotifyFn
 */

/**
 * @param {ComponentInternalInstance} [instance]
 */
function getNotifyFn(instance) {
  const globalProperties = get(instance, 'appContext.app.config.globalProperties');
  if (isFunction(globalProperties?.notify)) {
    return globalProperties.notify.bind(globalProperties);
  }

  return null;
}

/**
 * @returns NotifyFn
 */
function fallbackNotifyFn() {
  const mapTypeToConsole = { success: 'log', error: 'error', warning: 'warn', info: 'debug' };

  return (message, type = 'success', options = {}) => {
    console.error('[useNotify] Error getting notify function');
    console[mapTypeToConsole[type || 'success'] ?? 'log'](message, options);
  };
}

const defaultNotificationOptions = {
  duration: 5000,
  group: 'bottom-notifications',
  clean: false,
  actionText: null,
  actionFn: null,
  title: null
};

/**
 * Test here
 * @returns {{ notify: NotifyFn }}
 */
export function useNotify() {
  const vue = getCurrentInstance();
  const notifyFn = getNotifyFn(vue);

  if (!notifyFn) {
    console.error('[useNotify] Error getting notify function');
    return { notify: fallbackNotifyFn() };
  }

  const notify = (message, type = 'success', options = {}) => {
    const title = upperFirst(options.title ?? type);
    const text = sanitizeInput(message);
    const data = { ...notificationHelpers.getNotificationConfig(type), ...options };
    options = { ...defaultNotificationOptions, ...options, ...{ title, text, data, type } };
    notifyFn(text, type, options);
  };

  return { notify };
}
